import PropTypes from 'prop-types';
import ResourceDto from '../../../dtos/Resources/ResourceDto';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
    Autocomplete,
    CardMedia,
    Chip,
    createFilterOptions,
    FormLabel,
    GlobalStyles,
    Grid,
    TextField
} from '@mui/material';
import ResourceTopicDto from '../../../dtos/Resources/ResourceTopicDto';
import React, { useEffect, useState } from 'react';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ResourceApiService from '../../../api/ResourceApiService';
import * as resourceConstants from '../../../constants/resourceConstants';
import FileUploadModal from '../FileUploadModal';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    AutoLink,
    BlockQuote,
    Bold,
    ClassicEditor,
    Essentials,
    FontFamily,
    FontSize,
    Heading,
    Indent,
    Italic,
    Link,
    LinkEditing,
    LinkUI,
    List,
    MediaEmbed,
    Mention,
    Paragraph,
    Table,
    Undo
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import ConfirmDeleteDialog from '../ConfirmDelete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const ResourceTitle = 'title';
const ResourceDescription = 'description';

const inputGlobalStyles = (
    <GlobalStyles
        styles={{
            ".ck.ck-balloon-panel": {
                zIndex: "1400 !important", // Put a higher value that your MUI Dialog (generaly 1300)
            },
        }}
    />
);

export default function EditResourceModal(props) {
    const { resource, setResource, open, types, topics, selectableOrgs, cancel, resourceApiService } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const[showAttachmentModal, setShowAttachmentModal] = useState(false);
    const[attachmentType, setAttachmentType] = useState('');
    const[selectedTopics, setSelectedTopics] = useState([]);
    const[resourceTypeInputValue, setResourceTypeInputValue] = useState('');
    const[resourceTitleErrorMessage, setResourceTitleErrorMessage] = useState('');
    const[resourceImageErrorMessage, setResourceImageErrorMessage] = useState('');
    const[showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const[isPublic, setIsPublic] = useState(false);
    const[selectedOrgs, setSelectedOrgs] = useState([]);

    // Resolve a value coming back from a freeSolo Autocomplete (which can be a
    // plain string from `options`, the typed-but-unselected string, or our
    // { isNew, inputValue } sentinel pushed by filterOptions for the "Add «foo»"
    // entry) into the raw string the caller actually wants.
    function resolveAutocompleteValue(newValue) {
        if (newValue === null || newValue === undefined || newValue === '') return '';
        if (typeof newValue === 'object' && newValue.isNew) return newValue.inputValue;
        return newValue;
    }

    function changeResourceType(newValue) {
        const resourceCopy = {...resource};
        resourceCopy.resourceType = resolveAutocompleteValue(newValue);
        setResource(resourceCopy);
    }

    async function handleFileUpload (event) {
        if (event) {
            const attachmentResponse = await resourceApiService.addResourceAttachment(event);
            if(attachmentResponse.success) {
                const resourceCopy = {...resource};
                if(attachmentType === resourceConstants.ImageAttachment) {
                    resourceCopy.resourceImage = attachmentResponse.data;
                } else if (attachmentType === resourceConstants.FileAttachment) {
                    resourceCopy.resourceAttachment = attachmentResponse.data;
                }
                setResource(resourceCopy);
            }
        }

        setShowAttachmentModal(false);
    }

    function updateFieldByName(event, fieldName) {
        const resourceCopy = {...resource};

        switch (fieldName) {
        case ResourceTitle:
            resourceCopy.rsTitle = event.target.value;
            break;
        case ResourceDescription:
            resourceCopy.rsShortDescription = event.target.value;
            break;
        default:
            break;
        }

        setResource(resourceCopy);
    }

    function updateResourceContent(data) {
        const resourceCopy = {...resource};

        resourceCopy.rsContent = data;
        setResource(resourceCopy);
    }

    const { run: saveResource, loading: saving } = useAsyncCallback(async () => {
        const valid = validate();

        if(valid) {
            if(resource.rsId > 0) {
                const update = await resourceApiService.updateResource(resource);
                if(update.success) {
                    snackbar.success(t('editResource.toast.updated'));
                    cancel();
                } else {
                    snackbar.error(t('editResource.toast.updateError', { error: update.message }));
                }
            } else {
                const addResource = await resourceApiService.addResource(resource);
                if(addResource.success) {
                    snackbar.success(t('editResource.toast.added'));
                    setResource(null);
                    cancel();
                } else {
                    snackbar.error(t('editResource.toast.addError', { error: addResource.message }));
                }
            }
        }
    });

    function changeResourceTopics(newValues) {
        const value = newValues.map((entry) => {
            const name = resolveAutocompleteValue(entry);
            const foundTopic = topics.find((topic) => topic.name.toLowerCase() === name.toLowerCase());
            return foundTopic ?? new ResourceTopicDto(0, name);
        });

        const resourceCopy = {...resource};

        resourceCopy.resourceTopics = value;
        setResource(resourceCopy);
    }

    // Filter helper that appends a localized "Add «foo»" sentinel option when
    // the typed value doesn't match any existing option (freeSolo creatable pattern).
    function buildFilterWithAddOption(options, params) {
        const filtered = filter(options, params);
        const { inputValue } = params;
        const isExisting = options.some((option) => inputValue.toLowerCase() === option.toLowerCase());
        if (inputValue !== '' && !isExisting) {
            filtered.push({ isNew: true, inputValue, label: t('common.addNew', { value: inputValue }) });
        }
        return filtered;
    }

    // Autocomplete option labels can be plain strings (existing options) or
    // our sentinel object for the "Add «foo»" entry.
    function autocompleteOptionLabel(option) {
        if (typeof option === 'string') return option;
        if (option && option.label) return option.label;
        return '';
    }

    function changeAllowedOrgs(newValues) {
        const value = newValues
            .map((value) => selectableOrgs.find((org) => org.ogId === value.ogId))
            .filter(Boolean);

        const resourceCopy = {...resource};

        resourceCopy.ctOrgs = value;
        setSelectedOrgs(value);
        setResource(resourceCopy);
    }

    function changePublicVisibility() {
        const resourceCopy = {...resource};
        resourceCopy.isPublic = !isPublic;
        setResource(resourceCopy);
        setIsPublic(!isPublic);
    }

    function parseResourceTopicsForSelect() {
        let parsedSelectedTopics = [];

        resource?.resourceTopics?.forEach((value) => {

            parsedSelectedTopics.push(value.name);
        });

        return parsedSelectedTopics;
    }

    function validate() {
        let isValid = true;
        let titleError = '';
        let imageError = '';


        if(!resource.rsTitle) {
            isValid = false;
            titleError = t('editResource.validation.titleRequired');
        }
        if(!resource.resourceImage) {
            isValid = false;
            imageError = t('editResource.validation.imageRequired');
        }

        setResourceTitleErrorMessage(titleError);
        setResourceImageErrorMessage(imageError);

        if(titleError || imageError) {
            const errors = [titleError, imageError].filter(Boolean).join(', ');
            snackbar.error(t('editResource.toast.saveFailed', { errors }));
        }

        return isValid;
    }

    async function deleteAttachment(confirmed) {
        if(confirmed) {
            const response = await resourceApiService.deleteResourceAttachment(resource.resourceAttachment.id);
            if(response && response.success) {
                const resourceCopy = {...resource};
                resourceCopy.resourceAttachment = null;
                setResource(resourceCopy);
            } else {
                snackbar.error(t('editResource.toast.deleteAttachmentError', { error: response.message }));
            }
        }
        setShowConfirmDeleteModal(false);
    }

    useEffect(() => {
        if(open && resource) {
            setSelectedTopics(parseResourceTopicsForSelect());
            setIsPublic(resource.isPublic);
            setSelectedOrgs(resource.ctOrgs ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, resource]);

    const filter = createFilterOptions();

    return(
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: '150%' } }}
            maxWidth="md"
            fullWidth
            open={open}
            disableEnforceFocus={true}
        >
            <DialogTitle>{resource?.rsId > 0 ? t('editResource.title.edit', { name: resource.rsTitle }) : t('editResource.title.create')}</DialogTitle>
            <DialogContent dividers>
                <Grid container>
                    <Grid item md={12}>
                        <FormLabel error={!!resourceTitleErrorMessage}>{resourceTitleErrorMessage ? resourceTitleErrorMessage : t('editResource.field.title')}</FormLabel>
                        <br/>
                        <TextField
                            sx={{width: '100%'}}
                            value={resource?.rsTitle}
                            onChange={(event) => updateFieldByName(event, ResourceTitle)}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <FormLabel>{t('editResource.field.description')}</FormLabel>
                        <br/>
                        <TextField
                            sx={{width: '100%'}}
                            value={resource?.rsShortDescription}
                            onChange={(event) => updateFieldByName(event, ResourceDescription)}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <FormLabel>{t('editResource.field.type')}</FormLabel>
                        <br/>
                        <Autocomplete
                            freeSolo
                            value={resource?.resourceType}
                            onChange={(event, newValue) => {
                                changeResourceType(newValue);
                            }}
                            filterOptions={buildFilterWithAddOption}
                            getOptionLabel={autocompleteOptionLabel}
                            inputValue={resourceTypeInputValue}
                            onInputChange={(event, newInputValue) => {
                                setResourceTypeInputValue(newInputValue);
                            }}
                            options={types}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4 }}>
                        <FormLabel>{t('editResource.field.topics')}</FormLabel>
                        <br/>
                        <Autocomplete
                            freeSolo
                            multiple
                            renderInput={(params) => <TextField {...params} />}
                            options={topics.map((topic) => topic.name)}
                            getOptionLabel={autocompleteOptionLabel}
                            renderValue={(value, getItemProps) =>
                                value.map((option, index) => {
                                    const { key, ...itemProps } = getItemProps({ index });
                                    return(<Chip variant="outlined" key={key} label={option} {...itemProps} />);
                                })
                            }
                            onChange={(event, newValue) => {
                                changeResourceTopics(newValue);
                            }}
                            filterOptions={buildFilterWithAddOption}
                            value={selectedTopics}
                        />
                    </Grid>
                    <Grid item md={12} sx={{
                        marginTop: '4px',
                        ".ck-editor__editable_inline": {
                            height: "200px",
                            border: (theme) =>
                                theme.palette.mode === "light"
                                    ? "1px solid rgba(0, 0, 0, 0.20) !important"
                                    : "1px solid rgba(200, 200, 200, 0.25) !important",
                            borderTop: "1px !important",
                        },
                    }}>
                        <FormControlLabel sx={{ display: 'block' }} control={<Checkbox checked={isPublic} onChange={() => changePublicVisibility()}/>} label={t('editResource.field.public')} />
                        <Grid item md={12} sx={{marginTop: 4 }}>
                            <FormLabel>{t('editResource.field.allowedOrgs')}</FormLabel>
                            <br/>
                            <Autocomplete
                                disabled={isPublic}
                                multiple
                                renderInput={(params) => <TextField {...params} />}
                                options={selectableOrgs}
                                getOptionLabel={(option) => option.ogName }
                                isOptionEqualToValue={(option, value) => option?.ogId != null && value?.ogId != null && option.ogId === value.ogId}
                                renderTags={(value, getTagProps) =>
                                    !isPublic &&
                                    value.map((option, index) => (
                                        <Chip
                                            key={option.ogId}
                                            label={option.ogName}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                onChange={(event, newValue) => {
                                    changeAllowedOrgs(newValue);
                                }}
                                filterOptions={(options, params) => {
                                    return filter(options, params);
                                }}
                                value={selectedOrgs}
                            />
                        </Grid>
                        <FormLabel>{t('editResource.field.text')}</FormLabel>
                        {inputGlobalStyles}
                        <CKEditor
                            editor={ClassicEditor}
                            style={{ height: '400px' }}
                            config={{
                                toolbar: {
                                    items: [ 'heading',
                                        '|',
                                        'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
                                        '|',
                                        'bold', 'italic', 'link', 'bulletedList', 'numberedList',
                                        '|',
                                        'blockQuote', 'insertTable', 'mediaEmbed', 'codeBlock',
                                        '|',
                                        'todoList', 'outdent', 'indent', 'undo', 'redo' ]
                                },
                                plugins: [
                                    Bold, Essentials, Italic, Mention, Paragraph, Undo, Link, List, Heading, Indent, MediaEmbed, Table, FontFamily, FontSize, BlockQuote, AutoLink, LinkEditing, LinkUI
                                ],
                                link: {
                                    addTargetToExternalLinks: true,
                                },
                                mediaEmbed: {
                                    previewsInData: true,

                                }
                            }}
                            data={resource?.rsContent}
                            onChange={(event, editor) => {
                                updateResourceContent(editor.getData());
                            }}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <FormLabel error={!!resourceImageErrorMessage}>{resourceImageErrorMessage ? resourceImageErrorMessage : t('editResource.field.image')}</FormLabel>
                        <br/>
                        <Button variant={'contained'} onClick={() => {setShowAttachmentModal(true); setAttachmentType(resourceConstants.ImageAttachment)}}>{resource?.resourceImage ? t('common.change') : t('common.select')}</Button>
                        <CardMedia
                            component={'img'}
                            sx={{maxHeight: 200,padding: "1em 1em 0 1em", objectFit: 'contain', marginBottom: '10px'}}
                            image={resource?.resourceImage?.url}
                        />
                    </Grid>
                    <Grid item md={12} sx={{marginTop: 4}}>
                        <Grid container>
                            <Grid item md={12} sx={{marginTop: 4}}>
                                <FormLabel>{t('editResource.field.attachment')}</FormLabel>
                            </Grid>
                            <Grid item md={12}>
                                <Button
                                    variant={'contained'}
                                    onClick={() => {
                                        setShowAttachmentModal(true);
                                        setAttachmentType(resourceConstants.FileAttachment)
                                    }}
                                >
                                    {resource?.resourceAttachment ? t('common.change') : t('common.select')}
                                </Button>
                                {resource?.resourceAttachment &&
                                    <Button
                                        variant={'contained'}
                                        onClick={() => {
                                            setShowConfirmDeleteModal(true);
                                        }}
                                        sx={{ marginLeft: '5px'}}
                                        color={'error'}
                                    >
                                        {t('editResource.button.deleteAttachment')}
                                    </Button>
                                }

                            </Grid>
                            <Grid item md={12}>
                                <FormLabel>{resource?.resourceAttachment?.fileName}</FormLabel>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <DialogActions>
                    <Button variant={'contained'} color={'error'} onClick={cancel} disabled={saving}>{t('common.cancel')}</Button>
                    <SaveButton loading={saving} variant={'contained'} onClick={saveResource}>{t('common.save')}</SaveButton>
                </DialogActions>
            </DialogContent>
            <FileUploadModal onClose={handleFileUpload} open={showAttachmentModal} imageButton={true} />
            <ConfirmDeleteDialog
                onClose={(confirmed) => deleteAttachment(confirmed)}
                open={showConfirmDeleteModal}
                title={t('editResource.button.deleteAttachment')}
                content={t('editResource.confirmDeleteAttachment')}
            />
        </Dialog>
    )
}

EditResourceModal.propTypes = {
    resource: PropTypes.objectOf(ResourceDto),
    setResource: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    types: PropTypes.arrayOf(PropTypes.string),
    topics: PropTypes.arrayOf(ResourceTopicDto),
    cancel: PropTypes.func.isRequired,
    resourceApiService: PropTypes.objectOf(ResourceApiService).isRequired,
}
